// Production documents on a PO, from synced rows: the spec revision each line carries (for a draft, the one it
// would be sent with), whether its item has a newer released one, its files, what the vendor's documents page
// shows (the revision last marked as sent, po_sent_revision) and the vendor's confirmations. Make POs --- the same for
// each build's parts whose documents its CM gets (shared/build-docs.ts): pinned on a sent line (po_line.part_specs), a
// draft's worked out from its make order's BOM revision with their current revisions, as the server does.
import { useMemo } from "react";
import { buildDocParts, partSpecsOf } from "../../../../shared/build-docs.ts";
import type { DocAccess, FileVersion, Po, PoLine, PoSentRevision, SpecRevision, SpecRevisionFile } from "../../../../shared/db.gen.ts";
import { isOpen } from "../../../../shared/po.ts";
import { newerRevision } from "../../../../shared/po-docs.ts";
import { useBomStructure } from "../../lib/boms.ts";
import { useRows } from "../../lib/sync.ts";
import { useSpecIndex } from "../specs/lib.ts";

/** A line as the vendor was sent it (po_sent_revision.lines). */
export type SentLine = {
  id: string;
  position: number;
  sku: string;
  qty: number;
  spec_revision_id: string | null;
  /** Make POs --- the build's parts as sent (older snapshots have none). */
  parts?: { item_id: string; spec_revision_id: string | null }[];
};

/** A file of a released revision, with the kind it was released as (spec_revision_file.kind, frozen at release). */
export type LineFile = FileVersion & { kind: SpecRevisionFile["kind"] };

export type LineSpec = {
  /** The revision this line carries now (a draft's: the one it will be sent with). */
  revision: SpecRevision | null;
  /** Released after this line was sent, while the PO is open. */
  newer: SpecRevision | null;
  /** The revision's files in their released order (spec_revision_file.position, as the vendor's page lists them). */
  files: LineFile[];
  /** Packaging --- the kinds of those files (a dieline, artwork…: what the vendor can print from); null without a revision. */
  kinds: ReadonlySet<string> | null;
  /** Packaging --- the same for `newer` (whether Send update would get the vendor what it lacks); null without one. */
  newerKinds: ReadonlySet<string> | null;
  /** On the vendor's documents page: false until the line is in a revision marked as sent. */
  onPage: boolean;
  /** The revision the vendor's page shows for this line (differs from `revision` until the update is sent). */
  vendorRevision: SpecRevision | null;
  /** When the vendor confirmed having `vendorRevision`. */
  confirmedAt: string | null;
  /** Make POs --- the build's parts whose documents the CM gets, each like a line ([] for anything but a build). */
  parts: PartLineSpec[];
};
/** Make POs --- a build part's spec on its line: what it is, and its revision as a line's is shown. */
export type PartLineSpec = Omit<LineSpec, "parts"> & { itemId: string; sku: string; name: string; subassembly: boolean };

/** The revision of the PO the vendor's documents page shows: the one last marked as sent. */
export function useLastSent(poId: string | undefined): (PoSentRevision & { lines: SentLine[] }) | null {
  const sent = useRows("po_sent_revision");
  return useMemo(() => {
    const mine = sent.filter((s) => s.po_id === poId).sort((a, b) => b.sent_at.localeCompare(a.sent_at) || b.revision - a.revision)[0];
    return mine ? { ...mine, lines: (Array.isArray(mine.lines) ? mine.lines : []) as SentLine[] } : null;
  }, [sent, poId]);
}

export function useLineSpecs(po: Po | undefined, lines: PoLine[]) {
  const sx = useSpecIndex(); // a variant's spec is its product's
  const revisions = useRows("spec_revision");
  const revisionFiles = useRows("spec_revision_file");
  const versions = useRows("file_version");
  const access = useRows("doc_access");
  const lastSent = useLastSent(po?.id);
  const ix = useBomStructure(); // Make POs --- a draft's build parts
  const makeOrders = useRows("make_order");
  return useMemo(() => {
    const out = new Map<string, LineSpec>();
    if (!po) return out;
    const sent = po.sent_revision !== null;
    const revById = new Map(revisions.map((r) => [r.id, r]));
    const currentByItem = { get: (id: string) => revById.get(sx.specByItem.get(id)?.current_revision_id ?? "") };
    const versionById = new Map(versions.map((v) => [v.id, v]));
    const carried = lines.map((l) => {
      const current = currentByItem.get(l.item_id) ?? null;
      const revision = sent ? (l.spec_revision_id ? (revById.get(l.spec_revision_id) ?? null) : null) : current;
      return { l, revision, newer: sent && isOpen(po.status) ? newerRevision(revision, current, l.created_at ?? po.sent_at) : null };
    });
    // Make POs --- each build's parts: a sent line's as pinned (newer: any released since), else (a draft, or a line
    // sent before parts were pinned) as they'd be sent now.
    const builds = new Map(makeOrders.flatMap((m) => (m.po_id === po.id && m.bom_id ? [[m.po_line_id, m.bom_id] as const] : [])));
    const partsOf = (l: PoLine) => {
      const kept = sent ? partSpecsOf(l.part_specs) : null;
      if (kept)
        return kept.map((p) => {
          const revision = p.spec_revision_id ? (revById.get(p.spec_revision_id) ?? null) : null;
          const current = currentByItem.get(p.item_id) ?? null;
          return { itemId: p.item_id, revision, newer: isOpen(po.status) ? newerRevision(revision, current, new Date(0)) : null };
        });
      const bom = ix.bomById.get(builds.get(l.id) ?? "");
      const item = ix.itemById.get(l.item_id);
      if (!bom || !item) return [];
      return buildDocParts(ix, bom, item).map((p) => ({ itemId: p.id, revision: currentByItem.get(p.id) ?? null, newer: null }));
    };
    const builtParts = new Map(lines.map((l) => [l.id, partsOf(l)]));
    // Only the revisions these lines carry (and Packaging --- their newer ones), ordered like the server's
    // revisionFiles(): position, name, file name.
    const wanted = new Set<string>();
    for (const { revision, newer } of [...carried, ...[...builtParts.values()].flat()]) {
      if (revision) wanted.add(revision.id);
      if (newer) wanted.add(newer.id);
    }
    const byRev = new Map<string, { rf: SpecRevisionFile; v: FileVersion }[]>();
    for (const rf of revisionFiles) {
      const v = wanted.has(rf.spec_revision_id) ? versionById.get(rf.file_version_id) : undefined;
      if (v) byRev.set(rf.spec_revision_id, [...(byRev.get(rf.spec_revision_id) ?? []), { rf, v }]);
    }
    const filesByRev = new Map<string, LineFile[]>();
    for (const [id, list] of byRev) {
      list.sort((a, b) => a.rf.position - b.rf.position || a.rf.name.localeCompare(b.rf.name) || a.v.filename.localeCompare(b.v.filename));
      filesByRev.set(
        id,
        list.map(({ rf, v }) => ({ ...v, kind: rf.kind })),
      );
    }
    const confirmed = new Map<string, string>();
    for (const a of access) {
      if (a.po_id !== po.id || a.kind !== "ack" || !a.spec_revision_id) continue;
      const seen = confirmed.get(a.spec_revision_id);
      if (!seen || a.at < seen) confirmed.set(a.spec_revision_id, a.at);
    }
    const sentLines = new Map((lastSent?.lines ?? []).map((l) => [l.id, l]));
    const kindsOf = (r: SpecRevision | null) => (r ? new Set((filesByRev.get(r.id) ?? []).map((f) => f.kind)) : null);
    const view = (revision: SpecRevision | null, newer: SpecRevision | null, onPage: boolean, sentRevisionId: string | null | undefined) => {
      const vendorRevision = sentRevisionId ? (revById.get(sentRevisionId) ?? null) : null;
      return {
        revision,
        newer,
        files: revision ? (filesByRev.get(revision.id) ?? []) : [],
        kinds: kindsOf(revision),
        newerKinds: kindsOf(newer),
        onPage,
        vendorRevision,
        confirmedAt: vendorRevision ? (confirmed.get(vendorRevision.id) ?? null) : null,
      };
    };
    for (const { l, revision, newer } of carried) {
      const onPage = sentLines.get(l.id);
      const parts = (builtParts.get(l.id) ?? []).map((p) => {
        const sentPart = onPage?.parts?.find((x) => x.item_id === p.itemId);
        const item = ix.itemById.get(p.itemId);
        return {
          ...view(p.revision, p.newer, !!sentPart, sentPart?.spec_revision_id),
          itemId: p.itemId,
          sku: item?.sku ?? "",
          name: item?.name ?? "",
          subassembly: item?.kind === "subassembly",
        };
      });
      out.set(l.id, { ...view(revision, newer, !!onPage, onPage?.spec_revision_id), parts });
    }
    return out;
  }, [po, lines, sx, revisions, revisionFiles, versions, access, lastSent, ix, makeOrders]);
}

/** This PO's access log, newest first. */
export function usePoAccess(poId: string): DocAccess[] {
  const access = useRows("doc_access");
  return useMemo(() => access.filter((a) => a.po_id === poId).sort((a, b) => b.at.localeCompare(a.at)), [access, poId]);
}

/** "Nov 3, 14:02" */
export const when = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
